import React from "react";

import { DateRangePicker } from "../../../../components/ui";
import { ChevronLeft, ChevronRight } from "../../../../components/icons/svg/outline";
import { getInclusivePeriodEndDateKey } from "../../../../utils/datetime";
import {
  selectionFromDateBounds,
  shiftOverviewMetricsRange,
} from "./metricsHelpers";

const END_OFFSET_SUGGESTIONS = [
  { unit: "week", count: 1, label: "1 Week" },
  { unit: "week", count: 2, label: "2 Weeks" },
  { unit: "week", count: 3, label: "3 Weeks" },
  { unit: "week", count: 4, label: "4 Weeks" },
  { unit: "month", count: 1, label: "1 Month" },
  { unit: "month", count: 6, label: "6 Months" },
  { unit: "year", count: 1, label: "1 Year" },
];

/**
 * Project overview metrics date-range control (start/end + domain suggestions).
 *
 * Prev/next chevrons slide the whole window by one day or week to match Mode.
 *
 * @param {Object} props
 * @param {{key: string, start: string|null, end: string|null}} props.value - Active selection.
 * @param {(selection: {key: string, start: string|null, end: string|null}) => void} props.onChange
 * @param {"daily"|"weekly"} [props.granularity="daily"] - Chevron step size (day vs week).
 * @param {Array<{start: string, end: string, label: string}>} [props.startSuggestions=[]] Full-month chips.
 * @param {string} [props.ariaLabel="Date range"] - Accessible group label.
 * @returns {JSX.Element}
 */
const OverviewMetricsRangePicker = ({
  value,
  onChange,
  granularity = "daily",
  startSuggestions = [],
  ariaLabel = "Date range",
}) => {
  const startValue = value?.start || "";
  const shiftUnitLabel = granularity === "weekly" ? "week" : "day";

  const endSuggestions = startValue
    ? END_OFFSET_SUGGESTIONS.map(({ unit, count, label }) => ({
        key: `${unit}-${count}`,
        label,
        value: getInclusivePeriodEndDateKey(startValue, unit, count),
      }))
    : [];

  /**
   * Slide start and end together by one day or week.
   *
   * @param {-1|1} direction
   * @returns {void}
   */
  const handleShiftRange = (direction) => {
    onChange(shiftOverviewMetricsRange(value, granularity, direction));
  };

  return (
    <div
      className="flex shrink-0 items-end gap-1"
      role="group"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        aria-label={`Previous ${shiftUnitLabel}`}
        onClick={() => handleShiftRange(-1)}
        className="mb-0 inline-flex h-[34px] w-7 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
      </button>
      {/* Separators frame Start+End as one unit the chevrons slide together. */}
      <div
        className="mb-0 h-[34px] w-px shrink-0 bg-gray-300"
        aria-hidden
      />
      <DateRangePicker
        value={{ start: value?.start ?? null, end: value?.end ?? null }}
        onChange={({ start, end }) =>
          onChange(selectionFromDateBounds(start, end))
        }
        startSuggestions={startSuggestions}
        endSuggestions={endSuggestions}
        startSuggestionsTitle="Presets"
        endSuggestionsTitle="Time After Start Date"
        ariaLabel={ariaLabel}
      />
      <div
        className="mb-0 h-[34px] w-px shrink-0 bg-gray-300"
        aria-hidden
      />
      <button
        type="button"
        aria-label={`Next ${shiftUnitLabel}`}
        onClick={() => handleShiftRange(1)}
        className="mb-0 inline-flex h-[34px] w-7 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
      >
        <ChevronRight className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
};

export default OverviewMetricsRangePicker;
